import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { apiRecursIdFormatGetOptions } from "@/generated/api/@tanstack/react-query.gen.ts";
import ContainerInset from "@/components/container-inset.tsx";
import { PageErrorNoData } from "@/components/page-error.tsx";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout.tsx";
import RecurFormSkeleton from "@/app/(authenticated)/$merchantId/recurs/_components/recur-form-skeleton.tsx";
import RecurForm from "@/app/(authenticated)/$merchantId/recurs/_components/recur-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/recurs/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { selectedMerchant } = useMerchantProvider();
  const { id } = Route.useParams();

  const recurQuery = useQuery({
    ...apiRecursIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "",
      },
      path: { id: id },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  const handleOnUpdate = () => {
    recurQuery.refetch();
  };

  if (recurQuery.isPending) {
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Recurring Transactions",
            href: `/${selectedMerchant?.merchAccountId}/recurs`,
          },
          {
            title: "Detail",
            href: "",
          },
        ]}
      >
        <PageLayoutRoot>
          {/* Page Header */}
          <PageLayoutHeader
            title={"Edit Recurring Transaction"}
            description={"Update payment schedule and billing information"}
          />

          {/* Form */}
          <PageLayoutContent>
            <RecurFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );
  }

  if (recurQuery.error) return <PageErrorNoData />;

  if (!recurQuery.data) return <PageErrorNoData />;

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Recurring Transactions",
          href: `/${selectedMerchant?.merchAccountId}/recurs`,
        },
        {
          title: "Detail",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        {/* Page Header */}
        <PageLayoutHeader
          title={"Edit Recurring Transaction"}
          description={"Update payment schedule and billing information"}
        />

        {/* Form */}
        <PageLayoutContent>
          <RecurForm
            cardBin={recurQuery.data.cardBin}
            cardLastFour={recurQuery.data.cardLastFour}
            status={recurQuery.data.status}
            defaultValues={{
              id: recurQuery.data.recurId,
              referenceId: recurQuery.data!.referenceId!,
              billingFirstName: recurQuery.data?.billingFirstName ?? "",
              billingLastName: recurQuery.data?.billingLastName ?? "",
              billingAddress1: recurQuery.data?.billingAddress1 ?? undefined,
              billingAddress2: recurQuery.data?.billingAddress2 ?? undefined,
              billingCity: recurQuery.data?.billingCity ?? undefined,
              billingState: recurQuery.data?.billingState ?? undefined,
              billingCountry: recurQuery.data?.billingCountry ?? undefined,
              billingZip: recurQuery.data?.billingZip ?? undefined,
              billingPhone: recurQuery.data?.billingPhone ?? undefined,
              billingEmail: recurQuery.data?.billingEmail ?? undefined,
              billingCompany: recurQuery.data?.billingCompany ?? undefined,
              startDate: recurQuery.data!.startDate!,
              plan: recurQuery.data!.plan!,
              cardData: null,
            }}
            onUpdateSuccess={handleOnUpdate}
            onSuspendedSuccess={handleOnUpdate}
            onReopenSuccess={handleOnUpdate}
          />
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
